import { AutocompleteWithLabel, InputWithLabel } from '@adotel/components'
import {
  LazyQueryExecFunction,
  OperationVariables,
  useLazyQuery,
  useQuery
} from '@apollo/client'
import { CircularProgress } from '@material-ui/core'
import SearchIcon from '@material-ui/icons/Search'
import { matchSorter } from 'match-sorter'
import { useEffect, useMemo, useState } from 'react'
import InfiniteScroll from 'react-infinite-scroller'

import {
  ACCOMMODATION_QUERY,
  ACCOMMODATIONS_QUERY
} from '../../graphql/accommodation'
import { PROVIDERS_QUERY } from '../../graphql/providerQueries'
import { getTranslate } from '../../localization'
import { Accommodation } from '../../types/accommodation'
import { ProviderBrief } from '../../types/provider'
import {
  RateConnectionGenerateInput,
  StructuredData
} from '../../types/rateConnection'
import { RatePlanBrief } from '../../types/rateplan'
import { RoomTypeBrief } from '../../types/roomtype'
import ExpandableBox from './ExpandableBox'
import ExpandableBoxRowSkeleton from './ExpandableBoxRow.Skeleton'
import useStyles from './styles/ProviderSection.styles'

interface ProviderSectionProps {
  input: RateConnectionGenerateInput
  setInput: React.Dispatch<React.SetStateAction<RateConnectionGenerateInput>>
  autoCompleteForProvider: any
  setAutoCompleteForProvider: React.Dispatch<any>
  structuredDataAccommodation: StructuredData[]
  setStructuredDataAccommodation: React.Dispatch<
    React.SetStateAction<StructuredData[]>
  >
  selectedAccommodationID: string
  setSelectedAccommodationID: React.Dispatch<React.SetStateAction<string>>
  ratePlanData:
    | {
        ratePlans: RatePlanBrief[]
      }
    | undefined
  getRoomTypes: LazyQueryExecFunction<
    {
      roomTypes: RoomTypeBrief[]
    },
    OperationVariables
  >
  getRatePlans: LazyQueryExecFunction<
    {
      ratePlans: RatePlanBrief[]
    },
    OperationVariables
  >
  roomtypesData:
    | {
        roomTypes: RoomTypeBrief[]
      }
    | undefined
  loadingForRoomtypes: boolean
  isRoleProvider: boolean
  isRoleAccommodation: boolean
  loadingForRatePlan: boolean
}

export default function ProviderSection({
  input,
  setInput,
  autoCompleteForProvider,
  setAutoCompleteForProvider,
  structuredDataAccommodation,
  setStructuredDataAccommodation,
  selectedAccommodationID,
  setSelectedAccommodationID,
  ratePlanData,
  getRoomTypes,
  roomtypesData,
  loadingForRoomtypes,
  getRatePlans,
  isRoleProvider,
  isRoleAccommodation,
  loadingForRatePlan
}: ProviderSectionProps) {
  const classes = useStyles()
  const [searchInputValue, setSearchInputValue] = useState<string>('')

  const PAGE_SIZE = 30
  const [numberOfVisibleAccommodation, setNumberOfVisibleAccommodation] =
    useState<number>(PAGE_SIZE)

  const { data: providersData, loading: loadingForProvider } = useQuery<{
    providers: ProviderBrief[]
  }>(PROVIDERS_QUERY, {
    notifyOnNetworkStatusChange: true
  })

  const [getAccommodation] = useLazyQuery<{
    accommodation: Accommodation
  }>(ACCOMMODATION_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: (resp) => {
      setInput({
        ...input,
        providerCode: resp.accommodation.basicDetails.defaultProviderCode || ''
      })
    }
  })

  const [
    getAccommodations,
    { loading: loadingForAccommodations, data: accommodationsData }
  ] = useLazyQuery<{
    accommodations: Accommodation[]
  }>(ACCOMMODATIONS_QUERY, {
    notifyOnNetworkStatusChange: true,
    onCompleted: () => {
      if (isRoleAccommodation) {
        getAccommodation({
          variables: {
            propertyCode:
              accommodationsData?.accommodations[0].basicDetails.propertyCode
          }
        })
      }
    }
  })

  useEffect(() => {
    if (autoCompleteForProvider) {
      setInput({
        ...input,
        providerCode: autoCompleteForProvider && autoCompleteForProvider.code
      })
      getAccommodations({
        variables: {
          providerCode: autoCompleteForProvider.code
        }
      })
    }

    if (isRoleAccommodation) {
      getAccommodations()
    }
  }, [autoCompleteForProvider])

  const newFullListStructuredData = useMemo(() => {
    return matchSorter(structuredDataAccommodation, searchInputValue, {
      keys: ['name'],
      sorter: (rankedItems) =>
        rankedItems.sort((a, b) => a.item.name.localeCompare(b.item.name))
    })
  }, [searchInputValue, structuredDataAccommodation])

  const populateStructuredData = (
    accommodations: Accommodation[]
  ): StructuredData[] => {
    return accommodations.map((acc) => {
      let data: StructuredData = {
        id: acc.basicDetails.accommodationID || '',
        code: acc.basicDetails.propertyCode,
        displayCode: acc.basicDetails.displayPropertyCode || '',
        name: acc.basicDetails.name,
        checked: false,
        children: []
      }

      return data
    })
  }

  useEffect(() => {
    if (
      accommodationsData &&
      (autoCompleteForProvider || isRoleAccommodation)
    ) {
      const populatedData = populateStructuredData(
        accommodationsData.accommodations
      )

      setStructuredDataAccommodation(populatedData)
    }
  }, [accommodationsData, autoCompleteForProvider])

  useEffect(() => {
    if (!roomtypesData && !ratePlanData) return

    let updatedData = structuredDataAccommodation.map((item, index) => {
      const accIDIndex = structuredDataAccommodation.findIndex(
        (ele) => ele.displayCode === selectedAccommodationID
      )

      if (accIDIndex === index) {
        // STEP 1: Add RoomTypes
        let updatedChildren = item.children

        if (roomtypesData) {
          updatedChildren = roomtypesData.roomTypes.map((roomtype) => ({
            id: roomtype.id || '',
            code: roomtype.roomTypeCode,
            displayCode: roomtype.displayRoomTypeCode || '',
            name: roomtype.name,
            checked: item.checked ? true : false,
            children: []
          }))
        }

        // STEP 2: Add RatePlans to the RoomTypes
        if (ratePlanData) {
          updatedChildren = updatedChildren.map((child) => {
            const matchingRatePlans = ratePlanData.ratePlans.filter(
              (ratePlan) => ratePlan.displayRoomTypeCode === child.displayCode
            )

            const existingIds = new Set(child.children.map((c) => c.id))

            const newRatePlanChildren = matchingRatePlans
              .filter((rp) => !existingIds.has(rp.id))
              .map((ratePlan) => ({
                id: ratePlan.id || '',
                code: ratePlan.ratePlanCode,
                displayCode: ratePlan.displayRatePlanCode || '',
                name: ratePlan.name,
                checked: child.checked ? true : false,
                children: []
              }))

            return {
              ...child,
              children: [...child.children, ...newRatePlanChildren]
            }
          })
        }

        return {
          ...item,
          children: updatedChildren
        }
      }

      return item
    })

    setStructuredDataAccommodation(updatedData)
  }, [loadingForRoomtypes, loadingForRatePlan])

  const handleLoadMore = () => {
    setNumberOfVisibleAccommodation((currentValue) => currentValue + PAGE_SIZE)
  }

  return (
    <div>
      <div className={classes.providerSection}>
        <AutocompleteWithLabel
          noHelperText
          fullWidth
          value={autoCompleteForProvider}
          label={getTranslate('Provider')}
          placeholder={getTranslate('Select provider')}
          inputSize="small"
          options={
            providersData?.providers
              ? providersData?.providers.map((p) => ({
                  label: `${p.name} - (${p.displayProviderCode})`,
                  value: p.id,
                  code: p.providerCode
                }))
              : []
          }
          onChange={(_, v: any) => {
            if (v) {
              setAutoCompleteForProvider({
                value: v.value,
                label: v.label,
                code: v.code
              })
            } else {
              setAutoCompleteForProvider(null)
              setStructuredDataAccommodation([])
            }
          }}
          disabled={isRoleProvider || isRoleAccommodation}
          getOptionLabel={(option) => option.label || ''}
          getOptionSelected={(option, value) => option.value === value.value}
          loading={loadingForProvider}
          loadingText={getTranslate('Loading') + '...'}
        />
      </div>
      <div>
        <InputWithLabel
          placeholder={getTranslate('Search by accommodation name')}
          autoComplete="off"
          noHelperText
          fullWidth
          startIcon={<SearchIcon />}
          className={classes.searchInput}
          value={searchInputValue}
          onChange={(e) => setSearchInputValue(e.currentTarget.value)}
          disabled={isRoleAccommodation}
        />
      </div>
      <div className={classes.itemsBox}>
        <div className={classes.listView}>
          <InfiniteScroll
            pageStart={0}
            loadMore={handleLoadMore}
            hasMore={
              numberOfVisibleAccommodation <
              structuredDataAccommodation.length - 1
            }
            loader={
              <div key={0} className={classes.loadingContainer}>
                <CircularProgress size={32} />
              </div>
            }
            useWindow={false}>
            {newFullListStructuredData.length < 1 && loadingForAccommodations
              ? Array(10)
                  .fill('')
                  .map((_, i) => <ExpandableBoxRowSkeleton key={i} depth={1} />)
              : newFullListStructuredData
                  .slice(0, numberOfVisibleAccommodation)
                  .map((item, index) => (
                    <ExpandableBox
                      key={index}
                      data={item}
                      depth={1}
                      getRoomTypes={getRoomTypes}
                      getRatePlans={getRatePlans}
                      loadingForRoomtypes={loadingForRoomtypes}
                      setSelectedAccommodationID={setSelectedAccommodationID}
                      exactAccommodationLayer={
                        index ===
                        structuredDataAccommodation.findIndex(
                          (ele) => ele.id === selectedAccommodationID
                        )
                      }
                      structuredData={structuredDataAccommodation}
                      setStructuredData={setStructuredDataAccommodation}
                      providerCode={input.providerCode}
                      disabled={(() => {
                        const checkedItems = structuredDataAccommodation.filter(
                          (acc) => acc.indeterminate || acc.checked
                        )
                        if (checkedItems.length === 1) {
                          return checkedItems[0].code !== item.code
                        }
                        return false
                      })()}
                    />
                  ))}
          </InfiniteScroll>
        </div>
      </div>
    </div>
  )
}
